import React, { useEffect, useState } from "react";
import useApi from "../../CustomHooks/useApi";
import { auditLogsAPI } from "../../../api/apiClient";
import Loading from "../../../Loading";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowRight,
  faSyncAlt,
  faChevronDown,
  faChevronUp,
  faCopy,
  faCode,
  faUser,
  faRoute,
  faPen,
  faPlus,
  faTrash
} from "@fortawesome/free-solid-svg-icons";
import "../../../css/Views/UserActionLogsView.css";

const entityLabels = {
  Participant: "Uczestnik",
  Reservation: "Rezerwacja",
  Trip: "Wycieczka",
  Variant: "Wariant",
  Element: "Element",
  AccommodationElement: "Nocleg",
  AttractionElement: "Atrakcja",
  TransportElement: "Transport",
  IndividualTransportElement: "Transport indywidualny",
  TrekkingElement: "Trekking",
  InsuranceDetails: "Dane ubezpieczeniowe",
  ParticipantCorrection: "Korekta uczestnika",
  User: "Użytkownik",
  UserRole: "Rola użytkownika",
  TripUser: "Dostęp do wycieczki",
  MembershipMember: "Osoba członkowska",
  FinancialDocument: "Dokument finansowy",
  VariantCoordinator: "Osoba koordynatorska wariantu",
  VariantElement: "Powiązanie elementu z wariantem"
};

const fieldLabels = {
  PreferredName: "Nazwa używana",
  FirstName: "Imię",
  LastName: "Nazwisko",
  Email: "E-mail",
  PhoneNumber: "Numer telefonu",
  Prepayment: "Przedpłata",
  AmountPaid: "Wpłacono",
  Price: "Cena",
  Name: "Nazwa",
  Description: "Opis",
  Capacity: "Liczba miejsc",
  StartDate: "Data rozpoczęcia",
  EndDate: "Data zakończenia",
  RegistrationStartDate: "Początek zapisów",
  RegistrationEndDate: "Koniec zapisów",
  StatusId: "Status",
  Status: "Status",
  SettlementStatusEnum: "Status rozliczenia",
  IsInternalTrip: "Wyjazd specjalny",
  PrimaryCoordinatorId: "Główna osoba koordynatorska",
  VariantPhotoLink: "Zdjęcie wariantu",
  VariantPhotoThumbnailLink: "Miniatura wariantu",
  PhotoLink: "Zdjęcie",
  PhotoThumbnailLink: "Miniatura zdjęcia",
  DocumentNumber: "Numer dokumentu",
  GrossAmount: "Kwota brutto",
  Currency: "Waluta",
  Notes: "Uwagi",
  IsPaymentManuallyConfirmed: "Ręcznie potwierdzona płatność",
  ParticipantStatusId: "Status uczestnika"
};

const currencyFields = new Set(["Prepayment", "AmountPaid", "Price", "GrossAmount", "InvoiceAmount", "PricePerPerson", "Amount"]);

const formatValue = (value, fieldName) => {
  if (value == null) return "—";
  if (typeof value === "boolean") return value ? "Tak" : "Nie";
  if (currencyFields.has(fieldName) && typeof value === "number") {
    return `${value.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł`;
  }
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const getEntityIcon = (entity) => {
  if (entity === "Participant" || entity === "User" || entity === "MembershipMember") return faUser;
  if (entity === "Trip" || entity === "Variant") return faRoute;
  return faPen;
};

const operationMeta = {
  modified: { label: "Zmieniono", icon: faPen, className: "modified" },
  added: { label: "Dodano", icon: faPlus, className: "added" },
  deleted: { label: "Usunięto", icon: faTrash, className: "deleted" }
};

const AuditDiff = ({ data, rawJson, onCopy }) => {
  const [showJson, setShowJson] = useState(false);
  const changes = Array.isArray(data?.changes) ? data.changes : [];

  if (changes.length === 0) return null;

  return (
    <div className="audit-diff">
      <div className="audit-change-list">
        {changes.map((change, index) => {
          const operation = operationMeta[change.operation] || operationMeta.modified;

          return (
            <div className="audit-change-card" key={`${change.entity}-${index}`}>
              <div className="audit-change-card-header">
                <div className="audit-change-entity-icon">
                  <FontAwesomeIcon icon={getEntityIcon(change.entity)} />
                </div>
                <div className="audit-change-identity">
                  <div className="audit-change-label-line">
                    <strong className="audit-change-label">{change.label || "Bez nazwy"}</strong>
                    <span className={`audit-operation-badge ${operation.className}`}>
                      <FontAwesomeIcon icon={operation.icon} /> {operation.label}
                    </span>
                  </div>
                  <div className="audit-change-entity-type">{entityLabels[change.entity] || change.entity}</div>
                </div>
              </div>

              {change.fields && Object.keys(change.fields).length > 0 && (
                <div className="audit-fields-table">
                  <div className="audit-fields-row audit-fields-header">
                    <div>Pole</div>
                    <div>Wartość przed</div>
                    <div>Wartość po</div>
                  </div>
                  {Object.entries(change.fields).map(([fieldName, fieldChange]) => (
                    <div className="audit-fields-row" key={fieldName}>
                      <div className="audit-field-name">
                        <strong>{fieldLabels[fieldName] || fieldName}</strong>
                        {fieldLabels[fieldName] && <span>{fieldName}</span>}
                      </div>
                      <div className="audit-value audit-value-old">{formatValue(fieldChange.old, fieldName)}</div>
                      <div className="audit-value audit-value-new">{formatValue(fieldChange.new, fieldName)}</div>
                    </div>
                  ))}
                </div>
              )}

              {change.values && Object.keys(change.values).length > 0 && (
                <div className="audit-added-values">
                  {Object.entries(change.values).map(([fieldName, value]) => (
                    <div className="audit-added-value" key={fieldName}>
                      <span>{fieldLabels[fieldName] || fieldName}</span>
                      <strong>{formatValue(value, fieldName)}</strong>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="audit-diff-actions">
        <button type="button" className="json-toggle-btn" onClick={() => setShowJson((v) => !v)}>
          <FontAwesomeIcon icon={faCode} /> {showJson ? "Ukryj JSON" : "Pokaż JSON"}
        </button>
      </div>

      {showJson && (
        <div className="json-wrapper audit-json-wrapper">
          <div className="json-toolbar">
            <span className="json-title">Surowe dane JSON</span>
            <button type="button" className="copy-btn" onClick={() => onCopy(rawJson)} title="Kopiuj JSON">
              <FontAwesomeIcon icon={faCopy} />
            </button>
          </div>
          <pre className="json-block">{rawJson}</pre>
        </div>
      )}
    </div>
  );
};

const UserActionLogsView = () => {
  const { callApi, isLoading } = useApi();
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTrigger, setSearchTrigger] = useState(0);
  const [expandedRows, setExpandedRows] = useState(() => new Set());

  const fetchLogs = () => {
    callApi(
      () => auditLogsAPI.getUsersLogs({ page, pageSize }),
      (data) => {
        setLogs(data.items);
        setTotalCount(data.totalCount);
        setExpandedRows(new Set());
      }
    );
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchTrigger]);

  const maxPage = Math.ceil(totalCount / pageSize) || 1;

  const handleSearchClick = () => {
    setPage(1);
    setSearchTrigger((v) => v + 1);
  };

  const toggleRow = (id) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const parseOther = (other) => {
    if (other == null) return null;
    try {
      if (typeof other === "string") return JSON.parse(other);
      return other;
    } catch {
      return String(other);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      console.warn("Clipboard copy failed", e);
    }
  };

  return (
    <div className="audit-logs-view">
      <div className="audit-header">
        Logi
        <FontAwesomeIcon icon={faSyncAlt} className="refresh-icon" onClick={handleSearchClick} title="Odśwież" />
      </div>

      {isLoading && <Loading text="Ładowanie logów..." />}
      
      {!isLoading && logs.length === 0 && <p>Brak logów do wyświetlenia.</p>}

      {!isLoading && logs.length > 0 && (
        <>
          <div className="table-responsive">
            <table className="audit-logs-table">
              <thead>
                <tr>
                  <th className="col-date">Data</th>
                  <th className="col-user">Użytkownik</th>
                  <th className="col-action">Opis akcji</th>
                  <th className="col-expand" aria-label="Rozwiń" />
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => {
                  const isExpanded = expandedRows.has(log.id);
                  const otherParsed = parseOther(log.other);
                  const otherString = otherParsed == null ? "" : typeof otherParsed === "string" ? otherParsed : JSON.stringify(otherParsed, null, 2);
                  const hasStructuredChanges = otherParsed && typeof otherParsed === "object" && Array.isArray(otherParsed.changes) && otherParsed.changes.length > 0;

                  return (
                    <React.Fragment key={log.id}>
                      <tr
                        className={`log-row ${isExpanded ? "expanded" : ""}`}
                        onClick={() => toggleRow(log.id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") toggleRow(log.id);
                        }}
                        title={isExpanded ? "Zwiń szczegóły" : "Pokaż szczegóły"}
                      >
                        <td>{new Date(log.timestamp).toLocaleString("pl-PL")}</td>
                        <td>{log.userName ?? "—"}</td>
                        <td>{log.comment}</td>
                        <td className="expand-cell"><FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} /></td>
                      </tr>

                      {isExpanded && (
                        <tr className="log-details">
                          <td colSpan={4}>
                            {hasStructuredChanges ? (
                              <AuditDiff data={otherParsed} rawJson={otherString} onCopy={copyToClipboard} />
                            ) : otherString ? (
                              <div className="json-wrapper">
                                <div className="json-toolbar">
                                  <span className="json-title">other</span>
                                  <button type="button" className="copy-btn" onClick={(e) => { e.stopPropagation(); copyToClipboard(otherString); }} title="Kopiuj JSON">
                                    <FontAwesomeIcon icon={faCopy} />
                                  </button>
                                </div>
                                <pre className="json-block">{otherString}</pre>
                              </div>
                            ) : (
                              <em>Brak dodatkowych danych dla tego wpisu.</em>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="pagination-controls">
            {page > 1 && <div onClick={() => setPage((p) => p - 1)}><FontAwesomeIcon icon={faArrowLeft} /> Poprzednia</div>}
            <span>Strona {page} z {maxPage}</span>
            {page < maxPage && <div onClick={() => setPage((p) => p + 1)}>Następna <FontAwesomeIcon icon={faArrowRight} /></div>}
          </div>
        </>
      )}
    </div>
  );
};

export default UserActionLogsView;
